import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { CVLocalizationService } from '../../../reusables/services/cv-localization.service';
import { SuggestCoachingModalService } from './suggest-coaching-modal.service';
import { SuggestCoachingModalData } from './suggest-coaching-modal.models';
import { Inject } from 'cv-ng-metadata/core';

@Component({
  selector: 'cv-suggest-coaching-modal',
  templateUrl: './suggest-coaching-modal.component.html',
  styleUrls: ['./suggest-coaching-modal.component.scss']
})

export class SuggestCoachingModalComponent implements OnInit {
    @Input() selectedRow: any;
    @Input() moduleKey: string;
    @Input() groupID: number;
    @Input() modalOpen: boolean;
    @Input() dashKey: string;

    @ViewChild('hyperlinkPopover', { static: false }) hyperlinkPopover: NgbPopover;

    formData = {
        formID: null, // Which coaching form to use
        groupID: null, // Who will receive the session
        groupLevel: null, // set by onGroupLevelChanged().  This is the group level to "Assign to"
        reason: "",
        roleID: null, // set by onRoleChanged()
        startBy: null, // ISO-8601 string of the date to start by (UTC string, parse in the backend). Value sent to endpoint
        startByDate: this.getTimeToNext15Min(), // this is the local time value of above startBy.  Used for display in the input box.
        userIDs: [], // Only used if selectedAssignType == 2, set by onUserChanged()
        hasNotes: false,
        notes: "",
    };

    dateFormat = window["CVAngularDateFormat"];
    initialStartByTime = this.getTimeToNext15Min();
    selectedDate: any = this.getTimeToNext15Min();

    coachingForms;
    selectedGroupLevel;
    supportData: SuggestCoachingModalData;
    assignmentTypes = new Map<string, number>;
    CoachingLocalizationSection;
    isLoading = true; 
    selectedAssignType = 1; // 1 = Role and Level, 2 = User
    selectedUserList = [];
    hyperlinkTooltipTxt = "";
    specialCharTooltipTxt = "";
    linkDisplayTxtTemp = "";
    hyperlinkAddressTemp = "";
    validDisplayText = false;
    validURL = false;
    lastCursorPosition = null;
    readyToSubmit = false;

    constructor(
        private activeModal: NgbActiveModal,
        private cvLocalizationService: CVLocalizationService,
        private service: SuggestCoachingModalService,
    ) {

    }

    async ngOnInit() {
        this.formData.startBy = this.formData.startByDate.toISOString();

        await this.setSupportData();

        //Assignment Types Localization
        this.CoachingLocalizationSection = await this.cvLocalizationService.GetSection("Coaching");
        if (this.CoachingLocalizationSection != null) {
            this.assignmentTypes.set(this.CoachingLocalizationSection.get("Role_And_Level"), 1);
            this.assignmentTypes.set(this.CoachingLocalizationSection.get("User"), 2);
        }
        else {
            this.assignmentTypes.set("Role and Level", 1);
            this.assignmentTypes.set("User", 2);
        }
        this.hyperlinkTooltipTxt = this.getLocalizedString("Add_Hyperlink");
        this.specialCharTooltipTxt = this.getLocalizedString("Link_Display_Tooltip");

        this.isLoading = false;
    }

    close() {
        this.activeModal.close();
    }

    public async setSupportData() {
        if (this.groupID) {
            this.formData.groupID = this.groupID;
            
            this.supportData = await this.service.getSupportData(this.groupID);

            this.selectedGroupLevel = this.supportData.GroupLevels[0];
            this.formData.roleID = this.supportData.Roles[0].RoleID;
            this.formData.formID = 0;
           
            this.onGroupLevelChanged();
        }
    }

    onGroupLevelChanged() {
        this.formData.groupLevel = this.selectedGroupLevel.Level;
        this.checkValidForm();
    }

    onUserChanged(event) {
        this.formData.userIDs = [];
        this.selectedUserList = event;

        //loop through selected users and add to formData
        this.selectedUserList.forEach((id) => {
            this.formData.userIDs.push(id);
        });
        
        this.checkValidForm();
    }

    getCurrentDate(): Date {
        return new Date();
    }

    getTimeToNext15Min() {
        var now = this.getCurrentDate();
        var future = this.getCurrentDate();
        var nextBlock = Math.ceil(now.getMinutes() / 15);
        if (nextBlock >= 4) { // 46 minutes rolls over to the next hour
            future.setHours(now.getHours() + 1);
            future.setMinutes(0);
        } else {
            future.setMinutes(nextBlock * 15);
        }
        future.setSeconds(0);
        return future;
    }

    onDateChange(event) {
        this.formData.startByDate = event.current_date ? this.selectedDate : this.formData.startByDate;
        this.formData.startBy = this.formData.startByDate.toISOString();
        this.selectedDate = new Date(this.formData.startByDate); //new date will force calendar to update
    }

    checkValidForm() {
        let valid = true;
        if (this.formData.reason.length == 0)
            valid = false;
        if ((this.formData.groupID == null || this.formData.groupID == 0) && this.selectedAssignType == 1)
            valid = false;
        if ((this.formData.groupLevel == null || this.formData.groupLevel == 0) && this.selectedAssignType == 1)
            valid = false;
        if ((this.formData.roleID == null || this.formData.roleID == 0) && this.selectedAssignType == 1)
            valid = false;
        if ((this.formData.userIDs == null || this.formData.userIDs.length == 0) && this.selectedAssignType == 2)
            valid = false;
        if (!this.formData.startBy)
            valid = false;

        this.readyToSubmit = valid;
    }

    getLocalizedString(localKey: string) {
        return this.cvLocalizationService.getCachedText("Coaching", localKey);
    }

    validateDisplayText() {
        const text = this.linkDisplayTxtTemp;
        this.validDisplayText = !text.includes("<") && !text.includes(">"); 
    }

    validateHyperlinkAddress() {
        var url = this.hyperlinkAddressTemp;
        var pattern = /^https?:\/\/[\-A-Za-z0-9+&@#\/%?=~_|!:,.;]*[\-A-Za-z0-9+&@#\/%=~_|]$/;
        this.validURL = pattern.test(url);
    }

    addHyperlink(url) {
        const displayText = this.linkDisplayTxtTemp ? this.linkDisplayTxtTemp : url;
        const hyperlink = "<a contentEditable='false' href='" + url + "' target='_blank'>" + displayText + "</a>";
        const notesDiv = document.getElementById('notesTextArea');
        const br = "<div><br></div>";
        let before = "";
        let after = "";
        if (!notesDiv.firstChild || notesDiv.firstChild.nodeName !== 'DIV') {
            notesDiv.innerHTML = "<div>" + notesDiv.innerHTML + "</div>";
            this.lastCursorPosition += 5; //account for added div
        }
        if (this.lastCursorPosition !== null) {
            before = notesDiv.innerHTML.substring(0, this.lastCursorPosition);
            after = notesDiv.innerHTML.substring(this.lastCursorPosition);
        } else {
            //add to start if no cursor position
            after = notesDiv.innerHTML;
            if (after.startsWith('<div>')) after = after.substring(5);
        }
        notesDiv.innerHTML = before + hyperlink + after + (after.endsWith(br) ? "" : br);
        this.formData.notes = before + hyperlink + after + (after.endsWith(br) ? "" : br);
        this.closeHyperlinkPopup();
        notesDiv.blur();
    }

    onInputChange(event) {
        this.formData.notes = (event.target as HTMLDivElement).innerHTML;
    }

    closeHyperlinkPopup() {
        this.hyperlinkAddressTemp = "";
        this.linkDisplayTxtTemp = "";
        this.validDisplayText = false;
        this.validURL = false;
        this.hyperlinkPopover.close();
    }

    handleKeyupEvent(event) {
        if (event.key === 'Enter') {
            const notesDiv = document.getElementById('notesTextArea');
            if (!notesDiv.firstChild || notesDiv.firstChild.nodeName !== 'DIV') {
                notesDiv.innerHTML = "<div>" + notesDiv.firstChild.nodeValue + "</div><div><br></div>";
                this.lastCursorPosition += 21; //account for added div
                //place cursor at end
                const range = document.createRange();
                const sel = window.getSelection();
                range.setStart(notesDiv.lastChild, 0);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
        this.formData.notes = (event.target as HTMLDivElement).innerHTML;
        this.updateCursorPosition();
    }

    updateCursorPosition() {
        const selection = document.getSelection();
        if (selection.rangeCount) {
            //get the cursor position accounting for non-visible html tags
            const range = selection.getRangeAt(0);
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(document.getElementById('notesTextArea'));
            preCaretRange.setEnd(range.endContainer, range.endOffset);
            const tempEl = document.createElement("div");
            tempEl.appendChild(preCaretRange.cloneContents());
            this.lastCursorPosition = tempEl.innerHTML.length;
            //set to inside of new line divs 
            if (tempEl.lastElementChild && tempEl.lastElementChild.tagName == 'DIV')
                this.lastCursorPosition -= 6;
        }
    }

    handlePasteEvent(event) {
        const urlValid = /^https?:\/\/[\-A-Za-z0-9+&@#\/%?=~_|!:,.;]*[\-A-Za-z0-9+&@#\/%=~_|]$/;
        const text = event.clipboardData.getData('text/plain');
        const html = event.clipboardData.getData('text/html');
        if (urlValid.test(text)) {
            event.preventDefault();
            setTimeout(() => {
                this.addHyperlink(text);
            }, 0);
        }
        else if (html && html.includes('<div') && !html.includes("img")) {
            event.preventDefault();
            const notesDiv = document.getElementById('notesTextArea');
            let before = notesDiv.innerHTML.substring(0, this.lastCursorPosition);
            let after = notesDiv.innerHTML.substring(this.lastCursorPosition);
            if (after.startsWith('</div>')) {
                this.lastCursorPosition += 6;
                before = notesDiv.innerHTML.substring(0, this.lastCursorPosition);
                after = notesDiv.innerHTML.substring(this.lastCursorPosition);
            }
            if (before.endsWith('<div>')) {
                this.lastCursorPosition -= 5;
                before = notesDiv.innerHTML.substring(0, this.lastCursorPosition);
                after = notesDiv.innerHTML.substring(this.lastCursorPosition);
            }
            notesDiv.innerHTML = before + html + after;
            notesDiv.blur();
        }
        else if ((text == "" && html == "") || (html != "" && html.includes("img"))) {
            event.preventDefault();
        }
    }

    copyToClipboard(event) {
        //copy the links as html elements
        event.preventDefault();
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const container = document.createElement('div');
            container.appendChild(range.cloneContents());
            const child = container.firstChild as HTMLElement;
            //don't copy the wrapping div
            if (container.children.length == 1 && container.firstChild.nodeName === 'DIV')
                container.innerHTML = child.innerHTML;
            if (container.querySelector('a')) {
                event.clipboardData.setData('text/html', container.innerHTML);
            } else {
                event.clipboardData.setData('text/plain', selection.toString());
            }
            if (event.type === 'cut') range.deleteContents();
        }
    }

    onAssignTypeChanged(event) {
        this.selectedAssignType = event;
        if (this.selectedAssignType == 2) {
            this.formData.roleID = 0;
            this.formData.groupLevel = 0;
        }
        else {
            this.selectedGroupLevel = this.supportData.GroupLevels[0];
            this.formData.roleID = this.supportData.Roles[0].RoleID;
            this.formData.groupLevel = this.selectedGroupLevel.Level;

            this.selectedUserList = [];
            this.formData.userIDs = []; 
        }

        this.checkValidForm();
    }

    onCancel() {
        this.activeModal.dismiss();
    }

    onCreate() {
        if (this.formData.formID == null) {
            this.formData.formID = 0;
        }
        const { startByDate, ...sessionData } = this.formData;

        this.service.createNewSuggestedSession(sessionData);
        this.activeModal.close();
    }
}
